// Accounts Payable activation (SC-657 / AP-817) for the demo.
//
// Contoso Studio starts with an AP contract in the "Offer" state, so the
// payables tab shows the product promo with "Try for free" → terms dialog →
// activation. Activating flips the contract to "Active" (persisted for the
// browser session) and the module opens empty — no invoices, no approval rule
// sets — so visitors can walk through the setup from scratch. All other demo
// companies have AP active from the start.
import { DEMO_COMPANY_STUDIO_SRO } from "./seed/identifiers.ts";
import type { DemoCompanyId } from "./seed/identifiers.ts";

type Json = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

const STORAGE_KEY = "fidoo-demo-ap-activation-v1";

/** Companies whose AP contract starts as an offer waiting for activation. */
export const AP_OFFER_COMPANIES: readonly string[] = [DEMO_COMPANY_STUDIO_SRO];

function readActivated(): string[] {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
}

const memoryActivated = new Set<string>();

export function isApActive(companyId: string): boolean {
  if (!AP_OFFER_COMPANIES.includes(companyId)) return true;
  return memoryActivated.has(companyId) || readActivated().includes(companyId);
}

function activate(companyId: string) {
  memoryActivated.add(companyId);
  try {
    const next = [...new Set([...readActivated(), companyId])];
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private mode — the in-memory flag keeps the session working.
  }
}

const contractDocId = (companyId: string) => `demo-ap-contract-${companyId}`;

/** Rewrites each company role's AP contract to the current activation state. */
export function withApContracts<T extends { roles?: Json[] }>(ctx: T): T {
  return {
    ...ctx,
    roles: ctx.roles?.map((role) => {
      const companyId = role.roleBasicInfo?.masterRoleRef?.documentId as string | undefined;
      if (!companyId || !Array.isArray(role.contracts)) return role;
      return {
        ...role,
        contracts: [
          ...role.contracts.filter((c: Json) => c.productType !== "AP"),
          { productType: "AP", contractStatus: isApActive(companyId) ? "Active" : "Offer" },
        ],
      };
    }),
  };
}

/** bw-contracts/getDocumentList for LiveApContract (useActivateApProduct). */
export function apContractList(body: Json): Json | null {
  if (!body?.readDocuments?.LiveApContract) return null;
  const filtering: Json[] = body.filtering ?? [];
  const val = (attr: string) => filtering.find((f) => f.filteringAttribute === attr)?.attributeValue;
  const partyId = val("partyId") as string | undefined;
  const status = val("contractStatus") as string | undefined;
  const companies = partyId ? [partyId] : [...AP_OFFER_COMPANIES];
  const docs = companies
    .map((companyId) => ({ companyId, status: isApActive(companyId) ? "Active" : "Offer" }))
    .filter((c) => !status || c.status === status)
    .map(({ companyId, status: contractStatus }) => ({
      documentType: "LiveApContract",
      documentHeader: {
        id: contractDocId(companyId),
        documentType: "LiveApContract",
        definitionVersion: "1",
        _ts: Math.floor(Date.now() / 1000),
        _etag: '"demo-etag"',
      },
      contractBasicInfo: {
        entityData: {
          productType: "AP",
          contractStatus,
          contractTermsVersion: "AP_v1",
        },
      },
    }));
  return {
    documentList: docs,
    pagingResponse: { totalItemsCount: docs.length, hasNextPage: false, pageNumber: 1, pageSize: 20 },
  };
}

/** bw-contracts/createDocument ReqActivateApContract. */
export function apActivateContract(body: Json): Json | null {
  if (body?.documentType !== "ReqActivateApContract") return null;
  const contractId = String(body?.masterDocumentReference?.documentId ?? "");
  const companyId = AP_OFFER_COMPANIES.find((c) => contractDocId(c) === contractId);
  if (companyId) activate(companyId);
  const ts = Math.floor(Date.now() / 1000);
  return {
    documentType: "ReqActivateApContract",
    documentHeader: {
      id: `demo-req-activate-ap-${ts}`,
      documentType: "ReqActivateApContract",
      definitionVersion: "1",
      _ts: ts,
      _etag: `"demo-${ts}"`,
    },
  };
}

let termsUrl: string | null = null;

/** A one-page placeholder terms PDF (blob URL) so the terms link opens something. */
function demoTermsPdfUrl(): string {
  if (termsUrl) return termsUrl;
  const lines = [
    ["18", "Fidoo - Obchodni podminky sluzby Prijate faktury"],
    ["11", "Ukazkovy dokument demo prostredi (verze AP_v1)."],
    ["11", "Platne obchodni podminky najdete v aplikaci Fidoo pri aktivaci sluzby."],
  ];
  const content = lines
    .map(([size, text], i) => `BT /F1 ${size} Tf 72 ${760 - i * 28} Td (${text}) Tj ET`)
    .join("\n");
  const pdf = [
    "%PDF-1.4",
    "1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj",
    "2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj",
    "3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 595 842]/Contents 4 0 R/Resources<</Font<</F1 5 0 R>>>>>>endobj",
    `4 0 obj<</Length ${content.length + 1}>>stream\n${content}\nendstream endobj`,
    "5 0 obj<</Type/Font/Subtype/Type1/BaseFont/Helvetica>>endobj",
    "trailer<</Root 1 0 R>>",
    "%%EOF",
  ].join("\n");
  termsUrl = URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }));
  return termsUrl;
}

/** bw-contracts/getCurrentContractTermsVersion for the AP product. */
export function apContractTerms(body: Json): Json | null {
  if (body?.entityData?.productType !== "AP") return null;
  const url = demoTermsPdfUrl();
  return {
    productType: "AP",
    contractTermsVersion: "AP_v1",
    version: "1",
    validFrom: "2026-09-01",
    contractTermsFileLink: { cs: url, en: url, other: url },
  };
}
